import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { OAuthService } from 'angular-oauth2-oidc';
import { firstValueFrom } from 'rxjs';
import { environment } from '@env/environment';

/** Checks server-side sid validity independently of access-token expiry. */
@Injectable({ providedIn: 'root' })
export class SessionMonitorService {
  private readonly http = inject(HttpClient);
  private readonly oauth = inject(OAuthService);
  private readonly destroyRef = inject(DestroyRef);
  readonly error = signal<string | null>(null);
  readonly reauthenticating = signal(false);
  private timer: ReturnType<typeof setInterval> | undefined;
  private inFlight: Promise<void> | undefined;
  private refreshInFlight: Promise<string> | undefined;
  private closed = false;
  private readonly onFocus = () => { void this.checkNow(); };
  private readonly onVisible = () => {
    if (typeof document !== 'undefined' && document.visibilityState === 'visible') void this.checkNow();
  };

  constructor() { this.destroyRef.onDestroy(() => this.stop()); }

  start(): void {
    if (this.timer || this.closed || !this.oauth.getAccessToken()) return;
    this.timer = setInterval(() => { void this.checkNow(); }, 30_000);
    if (typeof window !== 'undefined') window.addEventListener('focus', this.onFocus);
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', this.onVisible);
    void this.checkNow();
  }

  stop(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = undefined;
    if (typeof window !== 'undefined') window.removeEventListener('focus', this.onFocus);
    if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', this.onVisible);
  }

  checkNow(): Promise<void> {
    if (this.inFlight) return this.inFlight;
    if (this.closed || !this.oauth.getAccessToken()) return Promise.resolve();
    const task = this.checkSession();
    this.inFlight = task.finally(() => { this.inFlight = undefined; });
    return this.inFlight;
  }

  /** Share the token exchange with checks, explicit logout and API 401 retries. */
  refreshAccessToken(): Promise<string> {
    if (this.refreshInFlight) return this.refreshInFlight;
    const refresh = this.oauth.refreshToken().then(() => this.oauth.getAccessToken());
    this.refreshInFlight = refresh.finally(() => { this.refreshInFlight = undefined; });
    return this.refreshInFlight;
  }

  private async checkSession(): Promise<void> {
    if (!this.oauth.hasValidAccessToken()) {
      if (!this.oauth.getRefreshToken()) { this.closeLocal(); return; }
      try { await this.refreshAccessToken(); }
      catch (error) {
        if (isRevokedRefresh(error)) this.closeLocal();
        else this.error.set('No se pudo comprobar la sesión. Reintenta cuando vuelva la conexión.');
        return;
      }
    }
    const token = this.oauth.getAccessToken();
    if (!token || !this.oauth.hasValidAccessToken()) { this.closeLocal(); return; }
    try {
      await firstValueFrom(this.http.get(environment.API_AUTH_BASE + '/op/session', {
        headers: new HttpHeaders({ Authorization: 'Bearer ' + token }),
      }));
      this.error.set(null);
    } catch (error) {
      if (isSessionRevoked(error)) { this.closeLocal(); return; }
      if (error instanceof HttpErrorResponse && error.status === 401 && !this.oauth.getRefreshToken()) {
        this.closeLocal();
      } else if (error instanceof HttpErrorResponse && error.status === 401 && this.oauth.getRefreshToken()) {
        try {
          const renewed = await this.refreshAccessToken();
          if (!renewed || !this.oauth.hasValidAccessToken()) throw new Error('Access token unavailable');
          await firstValueFrom(this.http.get(environment.API_AUTH_BASE + '/op/session', {
            headers: new HttpHeaders({ Authorization: 'Bearer ' + renewed }),
          }));
          this.error.set(null);
        } catch (retryError) {
          if (isSessionRevoked(retryError) || isRevokedRefresh(retryError) ||
            retryError instanceof HttpErrorResponse && retryError.status === 401) this.closeLocal();
          else this.error.set('No se pudo comprobar la sesión. Reintenta cuando vuelva la conexión.');
        }
      } else this.error.set('No se pudo comprobar la sesión. Reintenta cuando vuelva la conexión.');
    }
  }

  /** Returns false on a failed global logout: never claim global revocation then. */
  async logout(): Promise<boolean> {
    if (this.closed) return true;
    this.error.set(null);
    if (!this.oauth.hasValidAccessToken()) {
      try { await this.refreshAccessToken(); }
      catch { this.error.set('No se pudo cerrar la sesión global. Reintenta.'); return false; }
    }
    const token = this.oauth.getAccessToken();
    if (!token || !this.oauth.hasValidAccessToken()) {
      this.error.set('No se pudo cerrar la sesión global. Reintenta.'); return false;
    }
    try {
      await firstValueFrom(this.http.post(environment.API_AUTH_BASE + '/op/logout', null, {
        headers: new HttpHeaders({ Authorization: 'Bearer ' + token }), withCredentials: true,
      }));
      this.closeLocal();
      return true;
    } catch {
      this.error.set('No se pudo cerrar la sesión global. Reintenta.');
      return false;
    }
  }

  closeLocal(): void {
    if (this.closed) return;
    this.closed = true;
    this.reauthenticating.set(true);
    this.error.set(null);
    this.stop();
    this.oauth.logOut(true);
    this.oauth.initCodeFlow(undefined, { prompt: 'login' });
  }
}

function isSessionRevoked(error: unknown): boolean {
  return error instanceof HttpErrorResponse && error.status === 401 &&
    error.error?.error === 'session_revoked';
}

function isRevokedRefresh(error: unknown): boolean {
  return error instanceof HttpErrorResponse && (error.status === 401 ||
    error.status === 400 && error.error?.error === 'invalid_grant');
}
